"use client";

import { useEffect, useState } from "react";

// Don't rely on `typeof navigator !== "undefined"` alone: Node (v21+)
// exposes a global `navigator`, but its polyfill doesn't implement
// `onLine` at all. Checking `"onLine" in navigator` correctly detects that
// case, so this returns `true` on the server instead of `undefined` — the
// prior bug baked `undefined` (falsy) into the SSR HTML, flashing the
// offline banner on every page load before hydration corrected it.
function getIsOnline(): boolean {
  if (typeof navigator === "undefined" || !("onLine" in navigator)) {
    return true;
  }
  return navigator.onLine;
}

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(getIsOnline);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
}
